// The Wilds, walkable: she explores a meadow and woods; each fun game is a landmark with a
// door tile. Enter on a door plays the game. Nothing here is saved; it is a place, not a game.

import { h, clear } from '@kit/dom';
import { makeSurface, roundRect } from '@kit/canvas';
import { makeLoop } from '@kit/loop';
import { mapFromStrings, moveBody, drawMap, followCamera, makePad, tileAt, setTile, type Body, type Camera } from '@kit/tilemap';
import { drawSprite, type Facing } from '@avatar/render';
import { GAMES, lockInfo } from '../registry';
import { getSave, subscribe } from '../state';
import { activePet, drawPet } from '../pets';
import { topBar, toast } from '../ui';
import { go } from '../router';
import { WILDS_ROWS, LEGEND, SOLID_IDS, TILE, TILE_ID, DOOR_SLOTS, START, LANDMARKS, type LandmarkKind } from './wilds-map-data';

const SPEED = 92;
const VIEW_ROWS = 11;

export function wildsMapScreen(root: HTMLElement) {
  const map = mapFromStrings(WILDS_ROWS, LEGEND, TILE, SOLID_IDS as unknown as number[]);
  const funGames = GAMES.filter(g => g.category === 'fun');
  const doors = DOOR_SLOTS.map((slot, i) => ({ ...slot, game: funGames[i] })).filter(d => d.game);

  const bar = topBar('The Wilds', { back: 'wilds' });
  const stage = h('div', { style: { padding: '0 12px' } });
  const card = h('div.card', { style: { display: 'none', margin: '8px 12px 0' } });
  const hint = h('div.msg', null, 'Walk with the arrows. Stand on a door and press Enter.');
  root.append(bar, stage, card, hint);

  const S = makeSurface(stage, { height: VIEW_ROWS * TILE, draw: false });
  const pad = makePad(root, { vertical: true, action: 'Enter' });
  const cam: Camera = { x: 0, y: 0, w: S.w, h: VIEW_ROWS * TILE };

  const body: Body = { x: START.tx * TILE + 5, y: START.ty * TILE + 2, w: 14, h: 20, vx: 0, vy: 0 };
  let facing: Facing = 'down';
  let walkT = 0;
  let t = 0;
  let enterLatch = false;
  let onDoor: number | null = null;     // index into doors
  let flowers = 0;
  const trail: { x: number; y: number }[] = [];
  let flashUntil = 0;

  function renderCard() {
    clear(card);
    if (onDoor === null) { card.style.display = 'none'; return; }
    const d = doors[onDoor];
    const lock = lockInfo(d.game);
    card.style.display = '';
    card.append(
      h('h3', null, d.game.title),
      h('p.muted.small', null, d.game.blurb),
      h('div.row', { style: { marginTop: '8px' } },
        lock.unlocked
          ? h('button.btn.primary', { onclick: () => go(`play/${d.game.id}`) }, 'Play')
          : h('button.btn', { onclick: () => go('wilds') }, `Unlock for ${lock.needGems} `, h('span.dot.gem', { style: { display: 'inline-block' } })),
        lock.unlocked ? h('span.small.muted', null, 'or press Enter') : h('span.small.muted', null, 'Unlock it on the Wilds list.'),
      ),
    );
  }

  function update(dt: number) {
    t += dt;
    const dx = (pad.right ? 1 : 0) - (pad.left ? 1 : 0);
    const dy = (pad.down ? 1 : 0) - (pad.up ? 1 : 0);
    const len = Math.hypot(dx, dy) || 1;
    body.vx = (dx / len) * SPEED;
    body.vy = (dy / len) * SPEED;
    if (dx || dy) {
      facing = Math.abs(dx) >= Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : (dy > 0 ? 'down' : 'up');
      walkT += dt * 8;
      trail.push({ x: body.x, y: body.y });
      if (trail.length > 400) trail.shift();
    } else walkT = 0;
    moveBody(map, body, dt);
    followCamera(cam, map, body.x + body.w / 2, body.y + body.h / 2, 0.18);

    const cx = Math.floor((body.x + body.w / 2) / TILE), cy = Math.floor((body.y + body.h / 2) / TILE);
    const id = tileAt(map, cx, cy);
    // wild flowers
    if (id === TILE_ID.WILD) {
      setTile(map, cx, cy, TILE_ID.GRASS);
      flowers++;
      flashUntil = t + 0.6;
      toast(flowers >= 5 ? 'You found every flower' : `Wild flower ${flowers} of 5`);
    }
    // doors
    let here: number | null = null;
    if (id >= TILE_ID.DOOR0 && id < TILE_ID.DOOR0 + 6) { const slot = id - TILE_ID.DOOR0; here = doors.findIndex(d => DOOR_SLOTS.indexOf(DOOR_SLOTS[slot]) === DOOR_SLOTS.indexOf(d as any) || (d.tx === DOOR_SLOTS[slot].tx && d.ty === DOOR_SLOTS[slot].ty)); if (here < 0) here = null; }
    if (here !== onDoor) { onDoor = here; renderCard(); }
    // Enter
    if (pad.action && !enterLatch) {
      enterLatch = true;
      if (onDoor !== null) {
        const d = doors[onDoor];
        if (lockInfo(d.game).unlocked) go(`play/${d.game.id}`);
        else toast(`${d.game.title} is locked. Unlock it on the Wilds list.`);
      }
    }
    if (!pad.action) enterLatch = false;
  }

  function draw() {
    const c = S.ctx, W = S.w, H = S.h;
    cam.w = W; cam.h = H;
    c.fillStyle = '#9ad67e'; c.fillRect(0, 0, W, H);
    drawMap(c, map, cam, (id, sx, sy, size, tx, ty) => {
      switch (id) {
        case TILE_ID.PATH: case TILE_ID.START: c.fillStyle = '#e4cfa0'; c.fillRect(sx, sy, size, size); break;
        case TILE_ID.TREE: c.fillStyle = '#8a5a3b'; c.fillRect(sx + size * 0.4, sy + size * 0.55, size * 0.2, size * 0.45); c.fillStyle = '#4f9a4a'; c.beginPath(); c.arc(sx + size / 2, sy + size * 0.42, size * 0.42, 0, 7); c.fill(); c.fillStyle = '#5fbf6a'; c.beginPath(); c.arc(sx + size * 0.4, sy + size * 0.35, size * 0.18, 0, 7); c.fill(); break;
        case TILE_ID.WATER: c.fillStyle = '#7fd0f0'; c.fillRect(sx, sy, size, size); c.fillStyle = 'rgba(255,255,255,.35)'; c.fillRect(sx + 4, sy + 6 + Math.sin(t * 2 + tx + ty) * 2, size * 0.4, 2); break;
        case TILE_ID.ROCK: c.fillStyle = '#9a9aa3'; c.beginPath(); c.moveTo(sx + 3, sy + size - 4); c.lineTo(sx + size * 0.3, sy + 5); c.lineTo(sx + size * 0.7, sy + 3); c.lineTo(sx + size - 3, sy + size - 4); c.closePath(); c.fill(); break;
        case TILE_ID.FLOWER: { c.strokeStyle = '#4f9a4a'; c.lineWidth = 2; c.beginPath(); c.moveTo(sx + size / 2, sy + size - 3); c.lineTo(sx + size / 2, sy + size * 0.5); c.stroke(); c.fillStyle = ['#ee86b7', '#f5c542', '#f6f3ec', '#e2574c'][(tx * 7 + ty) % 4]; for (let i = 0; i < 5; i++) { c.beginPath(); c.arc(sx + size / 2 + Math.cos(i * 1.257) * 3.5, sy + size * 0.45 + Math.sin(i * 1.257) * 3.5, 2.5, 0, 7); c.fill(); } c.fillStyle = '#f5c542'; c.beginPath(); c.arc(sx + size / 2, sy + size * 0.45, 1.8, 0, 7); c.fill(); break; }
        case TILE_ID.WILD: { const bob = Math.sin(t * 3 + tx) * 2; c.strokeStyle = '#4f9a4a'; c.lineWidth = 2; c.beginPath(); c.moveTo(sx + size / 2, sy + size - 3); c.lineTo(sx + size / 2, sy + size * 0.4 + bob); c.stroke(); c.fillStyle = '#9b6be6'; for (let i = 0; i < 6; i++) { c.beginPath(); c.arc(sx + size / 2 + Math.cos(i * 1.047) * 5, sy + size * 0.4 + bob + Math.sin(i * 1.047) * 5, 3, 0, 7); c.fill(); } c.fillStyle = '#f5c542'; c.beginPath(); c.arc(sx + size / 2, sy + size * 0.4 + bob, 2.5, 0, 7); c.fill(); break; }
        case TILE_ID.FENCE: c.fillStyle = '#b48a5a'; c.fillRect(sx, sy + size * 0.35, size, 3); c.fillRect(sx, sy + size * 0.65, size, 3); c.fillRect(sx + size / 2 - 2, sy + size * 0.25, 4, size * 0.6); break;
        default: if (id >= TILE_ID.DOOR0) { c.fillStyle = '#e4cfa0'; c.fillRect(sx, sy, size, size); c.fillStyle = '#f5c542'; roundRect(c, sx + 4, sy + 4, size - 8, size - 8, 4); c.fill(); }
      }
    });
    // landmarks (drawn above their door tiles)
    for (const d of doors) {
      const sx = d.tx * TILE - cam.x, sy = d.ty * TILE - cam.y;
      if (sx < -120 || sx > W + 120 || sy < -120 || sy > H + 120) continue;
      const lm = LANDMARKS[d.game.id] ?? { kind: 'signpost' as LandmarkKind, label: d.game.title };
      const locked = !lockInfo(d.game).unlocked;
      drawLandmark(c, lm.kind, sx + TILE / 2, sy, locked);
      c.fillStyle = '#2a2a2e'; c.font = '800 12px Nunito, sans-serif'; c.textAlign = 'center'; c.textBaseline = 'alphabetic';
      roundRect(c, sx + TILE / 2 - 44, sy + TILE + 2, 88, 16, 5); c.fillStyle = 'rgba(255,255,255,.85)'; c.fill();
      c.fillStyle = '#2a2a2e'; c.fillText(locked ? `${lm.label}  ${lockInfo(d.game).needGems}` : lm.label, sx + TILE / 2, sy + TILE + 14);
      if (locked) { c.fillStyle = 'rgba(230,230,235,.45)'; c.beginPath(); c.ellipse(sx + TILE / 2, sy - 10, 44, 30, 0, 0, 7); c.fill(); }
    }
    // pet follows along the trail
    const pet = activePet(getSave());
    if (pet) {
      const p = trail[Math.max(0, trail.length - 28)] ?? { x: body.x - 18, y: body.y };
      drawPet(c, pet, p.x + body.w / 2 - cam.x, p.y + body.h - cam.y, 1.9, walkT > 0 ? Math.floor(walkT) % 4 : 0);
    }
    // her
    const frame = walkT > 0 ? Math.floor(walkT) % 4 : 0;
    c.fillStyle = 'rgba(0,0,0,.15)'; c.beginPath(); c.ellipse(body.x + body.w / 2 - cam.x, body.y + body.h - cam.y + 1, 8, 3.5, 0, 0, 7); c.fill();
    drawSprite(c, getSave().avatar, body.x + body.w / 2 - cam.x, body.y + body.h - cam.y, body.h / 32 * 1.15, frame, facing);
    if (t < flashUntil) { c.fillStyle = 'rgba(155,107,230,.18)'; c.fillRect(0, 0, W, H); }
    // minimap
    const mw = map.cols * 2, mh = map.rows * 2, mx = W - mw - 8, my = 8;
    c.fillStyle = 'rgba(42,42,46,.55)'; roundRect(c, mx - 3, my - 3, mw + 6, mh + 6, 4); c.fill();
    for (let y = 0; y < map.rows; y++) for (let x = 0; x < map.cols; x++) {
      const id = map.data[y * map.cols + x];
      c.fillStyle = id === TILE_ID.TREE ? '#2e7d4f' : id === TILE_ID.WATER ? '#4f8fe6' : id === TILE_ID.PATH || id === TILE_ID.START ? '#e4cfa0' : id === TILE_ID.ROCK || id === TILE_ID.FENCE ? '#9a9aa3' : '#9ad67e';
      c.fillRect(mx + x * 2, my + y * 2, 2, 2);
    }
    for (const d of doors) { c.fillStyle = lockInfo(d.game).unlocked ? '#f5c542' : '#f6f3ec'; c.fillRect(mx + d.tx * 2 - 1, my + d.ty * 2 - 1, 4, 4); }
    c.fillStyle = '#e2574c'; c.fillRect(mx + Math.floor((body.x + body.w / 2) / TILE) * 2 - 1, my + Math.floor((body.y + body.h / 2) / TILE) * 2 - 1, 4, 4);
  }

  function drawLandmark(c: CanvasRenderingContext2D, kind: LandmarkKind, x: number, y: number, locked: boolean) {
    c.save();
    if (locked) c.globalAlpha = 0.75;
    switch (kind) {
      case 'cave': c.fillStyle = '#6b6b75'; c.beginPath(); c.ellipse(x, y - 8, 34, 26, 0, Math.PI, 0); c.fill(); c.fillStyle = '#2a2a2e'; c.beginPath(); c.ellipse(x, y, 14, 18, 0, Math.PI, 0); c.fill(); c.fillStyle = '#9b6be6'; c.beginPath(); c.arc(x - 4, y - 8, 2, 0, 7); c.arc(x + 5, y - 10, 2, 0, 7); c.fill(); break;
      case 'crag': c.fillStyle = '#8a8a93'; c.beginPath(); c.moveTo(x - 36, y); c.lineTo(x - 18, y - 30); c.lineTo(x - 6, y - 14); c.lineTo(x + 8, y - 40); c.lineTo(x + 22, y - 12); c.lineTo(x + 36, y); c.closePath(); c.fill(); c.fillStyle = '#e2574c'; c.beginPath(); c.ellipse(x + 8, y - 44, 9, 5, 0, 0, 7); c.fill(); c.beginPath(); c.moveTo(x + 2, y - 46); c.lineTo(x - 8, y - 56); c.lineTo(x + 4, y - 50); c.fill(); break;
      case 'tent': c.fillStyle = '#e2574c'; c.beginPath(); c.moveTo(x - 30, y); c.lineTo(x, y - 36); c.lineTo(x + 30, y); c.closePath(); c.fill(); c.fillStyle = '#f6f3ec'; for (let i = -2; i <= 2; i += 2) { c.beginPath(); c.moveTo(x + i * 8 - 4, y); c.lineTo(x + i * 3, y - 30); c.lineTo(x + i * 8 + 4, y); c.fill(); } c.fillStyle = '#2a2a2e'; c.beginPath(); c.moveTo(x - 7, y); c.lineTo(x, y - 14); c.lineTo(x + 7, y); c.fill(); break;
      case 'table': c.fillStyle = '#8a5a3b'; c.beginPath(); c.ellipse(x, y - 10, 26, 12, 0, 0, 7); c.fill(); c.fillRect(x - 3, y - 10, 6, 10); for (let i = -1; i <= 1; i++) { c.fillStyle = ['#e2574c', '#4f8fe6', '#f5c542'][i + 1]; c.save(); c.translate(x + i * 12, y - 12); c.rotate(i * 0.3); c.fillRect(-5, -7, 10, 14); c.restore(); } break;
      case 'flagrun': for (let i = -1; i <= 1; i++) { const fx = x + i * 22; c.fillStyle = '#9a9aa3'; c.fillRect(fx - 1, y - 34, 3, 34); c.fillStyle = ['#4f8fe6', '#5fbf6a', '#e2574c'][i + 1]; c.beginPath(); c.moveTo(fx + 2, y - 34); c.lineTo(fx + 16, y - 28); c.lineTo(fx + 2, y - 22); c.fill(); } c.fillStyle = '#4f8fe6'; c.beginPath(); c.moveTo(x, y - 12); c.lineTo(x + 5, y - 6); c.lineTo(x, y); c.lineTo(x - 5, y - 6); c.closePath(); c.fill(); break;
      case 'arena': c.strokeStyle = '#b48a5a'; c.lineWidth = 5; c.beginPath(); c.ellipse(x, y - 8, 34, 16, 0, 0, 7); c.stroke(); c.fillStyle = '#5fbf6a'; c.beginPath(); c.arc(x - 12, y - 10, 6, 0, 7); c.fill(); c.fillStyle = '#f5c542'; c.beginPath(); c.arc(x + 12, y - 10, 6, 0, 7); c.fill(); break;
      default: c.fillStyle = '#8a5a3b'; c.fillRect(x - 2, y - 30, 4, 30); c.fillStyle = '#f08a3c'; roundRect(c, x - 22, y - 32, 44, 14, 4); c.fill();
    }
    c.restore();
  }

  const loop = makeLoop(update, draw);
  loop.start();
  const un = subscribe(() => { renderCard(); });

  return () => { loop.stop(); pad.destroy(); S.destroy(); un(); };
}
